import { useState, useEffect, useCallback } from "react";
import {
  partnerService,
  PartnerValidationResponse,
} from "@/services/partnerService";
import { PartnerDto } from "@/types/partner";
import { useDebouncedCallback } from "@/hooks/useDebounce";

export interface ReferralState {
  urlCode: string | null;
  formCode: string;
  isValid: boolean | null;
  partnerInfo: PartnerDto | null;
  isLocked: boolean;
  isValidating: boolean;
  message: string | null;
}

export const useReferralCode = (
  searchParams?: URLSearchParams,
  initialValue?: string,
) => {
  const [referralState, setReferralState] = useState<ReferralState>({
    urlCode: null,
    formCode: initialValue || "",
    isValid: null,
    partnerInfo: null,
    isLocked: false,
    isValidating: false,
    message: null,
  });

  const validateReferralCode = useCallback(async (code: string) => {
    try {
      const validation = await partnerService.validateReferralCode(code.trim());

      setReferralState((prev) => ({
        ...prev,
        isValid: validation.valid,
        partnerInfo: validation.partner || null,
        message: validation.message || null,
        isValidating: false,
      }));
    } catch (error) {
      console.error("Error validating referral code:", error);
      setReferralState((prev) => ({
        ...prev,
        isValid: false,
        partnerInfo: null,
        message: "Error validating referral code",
        isValidating: false,
      }));
    }
  }, []);

  // Direct validation without debouncing - for initial values
  const setAndValidateReferralCode = useCallback(
    (code: string) => {
      if (code.trim() === "") {
        setReferralState((prev) => ({
          ...prev,
          formCode: "",
          isValid: null,
          partnerInfo: null,
          message: null,
          isValidating: false,
        }));
        return;
      }

      setReferralState((prev) => ({
        ...prev,
        formCode: code,
        isValidating: true,
        isValid: null,
        partnerInfo: null,
        message: null,
      }));

      validateReferralCode(code);
    },
    [validateReferralCode],
  );

  useEffect(() => {
    if (initialValue && initialValue.trim() !== "") {
      setAndValidateReferralCode(initialValue.trim());
    }
  }, [initialValue, setAndValidateReferralCode]);

  const validateAndLockReferralCode = useCallback(
    async (code: string) => {
      setReferralState((prev) => ({
        ...prev,
        isValidating: true,
        urlCode: code,
        formCode: code,
        isLocked: true,
      }));

      await validateReferralCode(code);
    },
    [validateReferralCode],
  );

  // Initialize from URL on component mount (only if searchParams provided)
  useEffect(() => {
    if (!searchParams) return;

    const urlCode = searchParams.get("ref") || searchParams.get("partner");
    if (urlCode && urlCode.trim() !== "") {
      validateAndLockReferralCode(urlCode.trim());
    }
  }, [searchParams, validateAndLockReferralCode]);

  // Create debounced validation function (500ms delay)
  const debouncedValidateReferralCode = useDebouncedCallback(
    validateReferralCode,
    500,
  );

  const handleManualReferralChange = useCallback(
    (code: string) => {
      // Don't allow changes if locked from URL
      if (referralState.isLocked) return;

      setReferralState((prev) => ({
        ...prev,
        formCode: code,
        isValidating: code.trim() !== "",
        isValid: null,
        partnerInfo: null,
        message: null,
      }));

      // If empty, don't validate
      if (code.trim() === "") {
        setReferralState((prev) => ({
          ...prev,
          isValid: null,
          partnerInfo: null,
          message: null,
          isValidating: false,
        }));
        return;
      }

      // Use debounced validation
      debouncedValidateReferralCode(code);
    },
    [referralState.isLocked, debouncedValidateReferralCode],
  );

  const getSecureReferralCode = (): string | null => {
    if (referralState.urlCode && referralState.isValid) {
      return referralState.urlCode;
    }

    // If manual entry is valid, use it
    if (referralState.formCode && referralState.isValid === true) {
      return referralState.formCode.trim();
    }

    // No valid referral
    return null;
  };

  return {
    referralState,
    handleManualReferralChange,
    setAndValidateReferralCode,
    getSecureReferralCode,
  };
};
